import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import LockIcon from "@mui/icons-material/Lock";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import { Box, Stack, Tooltip, Typography } from "@mui/material";

import {
  getQuoteScoreLevelStyle,
  QUOTE_SCORE_MUTED_STYLE,
} from "components/QuoteScore";
import { EVOTRUX_BORDER_GREY, LIGHT_GREY } from "constants/index";

const CONTENT_INSET = "12px";
const CONTROL_SIZE = "13px";

export const QUOTE_SCORE_STATUS_COPY: Record<
  Exclude<QuoteScoreStatusIcon, "none">,
  string
> = {
  lock: "This score is frozen since the quote became unbookable.",
  check: "This score is final and will not change since the quote was booked.",
  circle: "This score is subject to change while the quote is still bookable.",
};

function StatusMarker({
  statusIcon,
  color,
}: {
  statusIcon: Exclude<QuoteScoreStatusIcon, "none">;
  color: string;
}) {
  const iconSx = { fontSize: 16, color, flexShrink: 0 };
  return (
    <Box
      component="span"
      aria-hidden="true"
      sx={{ display: "inline-flex", alignItems: "center" }}
    >
      {statusIcon === "check" && <CheckCircleIcon sx={iconSx} />}
      {statusIcon === "circle" && <RadioButtonUncheckedIcon sx={iconSx} />}
      {statusIcon === "lock" && <LockIcon sx={iconSx} />}
    </Box>
  );
}

export interface QuoteScoreStripProps {
  // null when Price hasn't been scored yet (no other quotes on the load) —
  // there's no overall verdict to color the bar with (§14/§18).
  level: QuoteScoreLevel | null;
  // A dead (declined/cancelled/expired/lost) quote's verdict is grey, not
  // color-coded, so attention stays on quotes that can still be acted on.
  muted?: boolean;
  statusIcon?: QuoteScoreStatusIcon;
  isOpen: boolean;
  onToggle: () => void;
}

/**
 * The verdict, and the control that opens the evidence beneath it
 * (docs/engineering/design/quote_competitiveness_scoring_design.md §14).
 */
export function QuoteScoreStrip({
  level,
  muted = false,
  statusIcon = "none",
  isOpen,
  onToggle,
}: QuoteScoreStripProps) {
  let style = null;
  if (level != null) {
    style = muted ? QUOTE_SCORE_MUTED_STYLE : getQuoteScoreLevelStyle(level);
  }
  const railColor = style?.color ?? EVOTRUX_BORDER_GREY;
  const backgroundColor = style?.tint ?? LIGHT_GREY;
  const hoverColor = style?.hoverTint ?? LIGHT_GREY;
  const textColor = style?.color ?? "text.secondary";

  return (
    <Box
      component="button"
      type="button"
      aria-expanded={isOpen}
      onClick={onToggle}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1,
        width: "100%",
        pl: CONTENT_INSET,
        pr: 1.5,
        py: 1.25,
        border: "1px solid",
        borderColor: "divider",
        borderLeft: `4px solid ${railColor}`,
        borderRadius: "4px",
        backgroundColor,
        cursor: "pointer",
        textAlign: "left",
        transition: "background-color 0.15s ease",
        "&:hover": { backgroundColor: hoverColor },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={0.75}>
        {level == null ? (
          <Typography
            sx={{ fontSize: "14px", fontWeight: 500, color: "text.secondary" }}
          >
            No other quotes on this load yet
          </Typography>
        ) : (
          <Stack direction="row" alignItems="center" spacing={0.75}>
            {!(statusIcon === "check" || statusIcon === "circle") && (
              <Box
                aria-hidden="true"
                sx={{
                  width: "9px",
                  height: "9px",
                  borderRadius: "50%",
                  flexShrink: 0,
                  backgroundColor: railColor,
                }}
              />
            )}
            {(statusIcon === "check" || statusIcon === "circle") && (
              <StatusMarker statusIcon={statusIcon} color={railColor} />
            )}
            <Typography
              sx={{
                fontSize: "15px",
                fontWeight: 700,
                lineHeight: 1,
                color: textColor,
              }}
            >
              {getQuoteScoreLevelStyle(level).label}
            </Typography>
          </Stack>
        )}
        {statusIcon === "lock" && (
          <StatusMarker statusIcon="lock" color={railColor} />
        )}
      </Stack>
      <Tooltip
        title={
          isOpen
            ? "Hide the price, history, and profile evidence behind this score"
            : "Show the price, history, and profile evidence behind this score"
        }
      >
        <Box
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: "2px",
            fontSize: CONTROL_SIZE,
            fontWeight: 600,
            whiteSpace: "nowrap",
            color: textColor,
          }}
        >
          {isOpen ? "Hide" : "See why"}
          {isOpen ? (
            <KeyboardArrowUpIcon sx={{ fontSize: 18, color: "inherit" }} />
          ) : (
            <KeyboardArrowDownIcon sx={{ fontSize: 18, color: "inherit" }} />
          )}
        </Box>
      </Tooltip>
    </Box>
  );
}
